import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineCalculate,
    MdOutlineLocationOn,
    MdOutlinePrint,
    MdSuperscript
} from 'react-icons/md';
import { IoMdRadioButtonOn, IoMdRadioButtonOff } from "react-icons/io";
import { useReactToPrint } from 'react-to-print';
import Header from 'components/Header';
import Button from 'components/Button2';
import TextInput from 'components/TextInput2';
import ScientificCalculator from 'components/ScientificCalculator';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import { flattenKatexUnderlines } from 'global/katexWrap';

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];
const DISABLE_PRINT = ['ReadingTown', 'College Planning Corp', 'A+ Program', 'MT Learning Center', 'PREPARE Test Prep', 'High Peak Education', 'WellEd Labs'];

function TestACTPreview() {
    const [testName, setTestName] = useState(null);
    const [testModules, setTestModules] = useState(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [isPrint, setIsPrint] = useState(false);
    const [calculator, setCalculator] = useState(null);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [fullPassage, setFullPassage] = useState<{
        html?: string;
        preview?: string;
    } | null>(null);
    const [printFullPassage, setPrintFullPassage] = useState<Record<string, { html?: string; }> | null>(null);
    const [highlighting, setHighlighting] = useState<Array<{
        passageId?: string;
        html?: string;
        preview?: string;
    }>>([]);
    const [printHighlighting, setPrintHighlighting] = useState(null);
    // Per-(org, test) clean-print allowlist flag from the preview endpoint.
    const [cleanPrintTest, setCleanPrintTest] = useState(false);

    const { user } = useAuthContext();
    // Clean version applies when an org-wide customization is set ("Test Clean Version
    // Print" or the ACT-only variant) OR this specific (org, test) pair is on the
    // per-test clean-print allowlist.
    const cleanVersionEnabled = cleanPrintTest || !!user.customizations.find(customization =>
        customization === 'Test Clean Version Print' || customization === 'Test Clean Version Print ACT Only'
    );
    const printRef = useRef(null);
    const navigate = useNavigate();
    const { testId } = useParams();
    const moduleSelectRef = useOutsideClickHandler(() => setTimeout(() => setModuleSelectOpen(false), 10));
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));
    const passageContainerRef = useRef(null);

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
        else if (moduleIndex > 0) {
            setQuestionIndex(testModules[moduleIndex - 1].questions.length - 1);
            setModuleIndex(moduleIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < testModules[moduleIndex].questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else if (moduleIndex < testModules.length - 1) {
            setModuleIndex(moduleIndex + 1);
            setQuestionIndex(0);
        }
        else {
            // exit the review
            navigate('/tests');
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let passages = document.getElementById('testACTPreview__passages');
        let question = document.getElementById('testACTPreview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    }

    const generateHightlightIndexing = ({id: passageId, indices, passageFigures = []}) => {
        for (let j = 0; j < indices.length; j++){
            const passage = document.getElementById(passageId);
            let passageHTML = '';
            let passagePreview = '';
            const usedFigureIndices = new Set();
            const { startIndex, endIndex } = indices[j];
            let startOffset = startIndex
            let endOffset = endIndex
            let lastIndex = 0;

            for (let i = 0; i < passage.childNodes.length; i++) {
                const childNode = passage.childNodes[i];

                const addMarkTags = (node) => {
                    if (node.outerHTML) {
                        const { innerHTML, outerHTML } = node;
                        const childOffset = outerHTML.indexOf('>') + 1;
    
                        passageHTML += outerHTML.slice(0, childOffset);
    
                        for (let i = 0; i < node.childNodes.length; i++) {
                            const child = node.childNodes[i];
                            addMarkTags(child);
                        }
    
                        passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                    }
                    else {
                        // 이미지 삽입을 위한 passageFigure 검사
                        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                        if (currentFigure) {
                            usedFigureIndices.add(currentFigure.figure.index);
                            // passageHTML += `
                            //     <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                            // `;                
                            passageHTML += `
                                <img src="${currentFigure.figure.image}" alt="figure" style="width: auto; height: auto; max-width:100%; margin: 10px 0;" /></br>
                            `;        
                        }
                        const { textContent } = node;
    
                        if (startOffset >= lastIndex && endOffset <= lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                        }
                        else if (startOffset >= lastIndex && startOffset < lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(startOffset - lastIndex, textContent.length);
                            passageHTML += '</mark>';
                            passagePreview += textContent.slice(startOffset - lastIndex, textContent.length);
                        }
                        else if (endOffset >= lastIndex && endOffset < lastIndex + textContent.length) {
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(0, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(0, endOffset - lastIndex);
                        }
                        else if (startOffset <= lastIndex && endOffset >= lastIndex + textContent.length) {
                            passageHTML += '<mark>';
                            passageHTML += textContent;
                            passageHTML += '</mark>';
                            passagePreview += textContent;
                        }
                        else {
                            passageHTML += textContent;
                        }
    
                        lastIndex += textContent.length;
                    }
                }
    
                addMarkTags(childNode);
            }

            const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
            if (currentFigure) {
                // passageHTML += `
                //     <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                // `;     
                passageHTML += `
                    <img src="${currentFigure.figure.image}" alt="figure" style="width: auto; height: auto; max-width:100%; margin: 10px 0;" /></br>
                `;                   
            }

            setHighlighting(prev => [...(prev || []), { 
                passageId: passageId,
                html: passageHTML, 
                preview: passagePreview 
            }]);
        }

    };

    const getPassageWithFigure = ({id: passageId, passageFigures = []}) => {
        const passage = document.getElementById(passageId);
        let passageHTML = '';
        let lastIndex = 0;
        const usedFigureIndices = new Set();

        for (let i = 0; i < passage.childNodes.length; i++) {
            const childNode = passage.childNodes[i];
            // add open tags first while see the text
            const addMarkTags = (node) => {
                if (node.outerHTML) {
                    const { innerHTML, outerHTML } = node;
                    const childOffset = outerHTML.indexOf('>') + 1;

                    passageHTML += outerHTML.slice(0, childOffset);

                    for (let i = 0; i < node.childNodes.length; i++) {
                        const child = node.childNodes[i];
                        addMarkTags(child);
                    }

                    passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                } 
                else {
                    // 이미지 삽입을 위한 passageFigure 검사
                    const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                    if (currentFigure) {
                        usedFigureIndices.add(currentFigure.figure.index);
                        // passageHTML += `
                        //     <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                        // `;                
                        passageHTML += `
                            <img src="${currentFigure.figure.image}" alt="figure" style="width: auto; height: auto; max-width:100%; margin: 10px 0;" /></br>
                        `;        
                    }
                    const { textContent } = node;
                    passageHTML += textContent;
                    lastIndex += textContent.length;
                }  
            }

            addMarkTags(childNode);
        }

        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
        if (currentFigure) {
            // passageHTML += `
            //     <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
            // `;                    
            passageHTML += `
                <img src="${currentFigure.figure.image}" alt="figure" style="width: auto; height: auto; max-width:100%; margin: 10px 0;" /></br>
            `;    
        }

        setFullPassage({
            html: passageHTML
        });

    };

    const getArrayPassageWFigure = () => {
        let newPassages = {}; // 새로운 `printFullPassage` 데이터를 저장할 객체
    
        for (let i = 0; i < testModules.length; i++) {
            const module = testModules[i];
    
            for (let j = 0; j < module.questions.length; j++) {
                const question = module.questions[j];
                if (question.passages) {
                    const id = question.passages.id;
                    const passageFigures = question.passages.passageFigures || [];
                    if (!newPassages[id]) {
                        const passage = document.getElementById(`print ${id}`);
                        let passageHTML = '';
                        let lastIndex = 0;
                
                        const usedFigureIndices = new Set();

                        for (let i = 0; i < passage.childNodes.length; i++) {
                            const childNode = passage.childNodes[i];
                            // add open tags first while see the text
                            const addMarkTags = (node) => {
                                if (node.outerHTML) {
                                    const { innerHTML, outerHTML } = node;
                                    const childOffset = outerHTML.indexOf('>') + 1;
                
                                    passageHTML += outerHTML.slice(0, childOffset);
                
                                    for (let i = 0; i < node.childNodes.length; i++) {
                                        const child = node.childNodes[i];
                                        addMarkTags(child);
                                    }
                
                                    passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                                } 
                                else {
                                    // 이미지 삽입을 위한 passageFigure 검사
                                    const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                                    if (currentFigure) {
                                        usedFigureIndices.add(currentFigure.figure.index);
                                        // passageHTML += `
                                        //     <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                                        // `;           
                                        passageHTML += `
                                            <img src="${currentFigure.figure.image}" alt="figure" style="width: auto; height: auto; max-width:100%; margin: 10px 0;" /></br>
                                        `;             
                                    }
                                    const { textContent } = node;
                                    passageHTML += textContent;
                                    lastIndex += textContent.length;
                                }  
                            }
                
                            addMarkTags(childNode);
                        }
                
                        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                        if (currentFigure) {
                            // passageHTML += `
                            //     <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                            // `;            
                            passageHTML += `
                                <img src="${currentFigure.figure.image}" alt="figure" style="width: auto; height: auto; max-width:100%; margin: 10px 0;" /></br>
                            `;            
                        }
            
                        newPassages[id] = {
                            html: passageHTML
                        };
                    }
                }
            }
        }
        setPrintFullPassage(newPassages); // 최종 업데이트
    };

    const generatePrintHighlightIndexing = () => {
        let newHighlighting = {};

        for (let i = 0; i < testModules.length; i++) {
            const module = testModules[i];
    
            for (let j = 0; j < module.questions.length; j++) {
                const question = module.questions[j];
                if (question.passages) {
                    const id = question.passages.id;
                    const passage = document.getElementById(`print ${id}`);
                    const passageFigures = question.passages.passageFigures || [];
                    const indices = question.passages.indices;

                    for (let k = 0; k < indices.length; k++) {
                        let passageHTML = '';
                        let usedFigureIndices = new Set();
                        const { startIndex, endIndex } = indices[k];
                        let startOffset = startIndex
                        let endOffset = endIndex
                        let lastIndex = 0;
                        let firstMark = false;

                        for (let i = 0; i < passage.childNodes.length; i++) {
                            const childNode = passage.childNodes[i];
            
                            const addMarkTags = (node) => {
                                if (node.outerHTML) {
                                    const { innerHTML, outerHTML } = node;
                                    const childOffset = outerHTML.indexOf('>') + 1;
                
                                    passageHTML += outerHTML.slice(0, childOffset);
                
                                    for (let i = 0; i < node.childNodes.length; i++) {
                                        const child = node.childNodes[i];
                                        addMarkTags(child);
                                    }
                
                                    passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                                }
                                else {
                                    // 이미지 삽입을 위한 passageFigure 검사
                                    const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                                    if (currentFigure) {
                                        usedFigureIndices.add(currentFigure.figure.index);
                                        // passageHTML += `
                                        //     <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                                        // `;           
                                        passageHTML += `
                                            <img src="${currentFigure.figure.image}" alt="figure" style="width: auto; height: auto; max-width:100%; margin: 10px 0;" /></br>
                                        `;           
                                    }
                                    const { textContent } = node;
                
                                    if (startOffset >= lastIndex && endOffset <= lastIndex + textContent.length) {
                                        passageHTML += textContent.slice(0, startOffset - lastIndex);
                                        passageHTML += '<mark>';
                                        if (!firstMark) {
                                            passageHTML += `<span>${j+1}.</span>`;
                                            firstMark = true;
                                        }
                                        passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                                        passageHTML += '</mark>';
                                        passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                                        // passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                                    }
                                    else if (startOffset >= lastIndex && startOffset < lastIndex + textContent.length) {
                                        passageHTML += textContent.slice(0, startOffset - lastIndex);
                                        passageHTML += '<mark>';
                                        if (!firstMark) {
                                            passageHTML += `<span>${j+1}.</span>`;
                                            firstMark = true;
                                        }
                                        passageHTML += textContent.slice(startOffset - lastIndex, textContent.length);
                                        passageHTML += '</mark>';
                                        // passagePreview += textContent.slice(startOffset - lastIndex, textContent.length);
                                    }
                                    else if (endOffset >= lastIndex && endOffset < lastIndex + textContent.length) {
                                        passageHTML += '<mark>';
                                        passageHTML += textContent.slice(0, endOffset - lastIndex);
                                        passageHTML += '</mark>';
                                        passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                                        // passagePreview += textContent.slice(0, endOffset - lastIndex);
                                    }
                                    else if (startOffset <= lastIndex && endOffset >= lastIndex + textContent.length) {
                                        passageHTML += '<mark>';
                                        if (!firstMark) {
                                            passageHTML += `<span>${j+1}.</span>`;
                                            firstMark = true;
                                        }
                                        passageHTML += textContent;
                                        passageHTML += '</mark>';
                                        // passagePreview += textContent;
                                    }
                                    else {
                                        passageHTML += textContent;
                                    }
                
                                    lastIndex += textContent.length;
                                }
                            }
                
                            addMarkTags(childNode);
                        }
            
                        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                        if (currentFigure) {
                            //  passageHTML += `
                            //     <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                            // `;                   
                            passageHTML += `
                                <img src="${currentFigure.figure.image}" alt="figure" style="width: auto; height: auto; max-width:100%; margin: 10px 0;" /></br>
                            `;    
                        }

                        if (id in newHighlighting) {
                            newHighlighting[id].push(passageHTML);
                        }
                        else {
                            newHighlighting[id] = [passageHTML];
                        }
                    }
                }
            }
        }

        setPrintHighlighting(newHighlighting);
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            if (testModules[moduleIndex].subject === 'ACT English' || testModules[moduleIndex].subject === 'ACT Reading'|| testModules[moduleIndex].subject === 'ACT Science') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        if (!header) {
            return; 
        }
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        }

        const dragElement = (event) => {
            event.preventDefault();

            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }
        }

        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        }
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    }

    const printLog = () => {
        axios.post('tutors/tests/printLog/', {
                'testId': testId,
                'cleanVersion': (!showAnswer && cleanVersionEnabled) ? true : false
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onAfterPrint: () => {
            setIsPrint(false);
            printLog();
        }
    });

    const onPrint = () =>{
        setIsPrint(true);
    }

    useEffect(()=>{
        if(isPrint) {
            renderKatex()
            getArrayPassageWFigure()
            generatePrintHighlightIndexing()
        }
    }, [isPrint]);

    useEffect(() => {
        if (printFullPassage !== null && printHighlighting !== null) {
            handlePrint();
        }
    }, [printFullPassage, printHighlighting]);

    useEffect(() => {
        axios.get(`tests/digital/act/preview/${testId}/`)
            .then(res => {
                setTestName(res.data.name)
                setTestModules(res.data.testModules);
                setCleanPrintTest(res.data.cleanPrintTest);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    // passage scroll, passage magnifier scroll (according to the highliting index)
    useLayoutEffect(() => {
        if (highlighting.length > 0) {
            const passageId = highlighting[0].passageId;
            const passage = document.getElementById(`highlighting ${passageId}`);
            // highlighting 여러개일 경우 첫 번째 
            if (passage) {
                let firstHighlight = passage.querySelector("mark"); // 첫 번째 mark 태그 찾기
                let offset = 0;
                while (firstHighlight && firstHighlight !== passage) {
                    offset = firstHighlight.offsetTop;
                    firstHighlight = firstHighlight.parentElement;
                }
                if (firstHighlight && passageContainerRef.current) {
                    passageContainerRef.current.scrollTo({
                        top: firstHighlight? offset - passageContainerRef.current.offsetTop: 0,
                        behavior: "smooth", 
                    });
                }
            }
        }
    }, [highlighting]);

    useEffect(() => {
        if (testModules) {
            let calculatorEl = document.getElementById('calculator');
            setCalculator(Desmos.GraphingCalculator(calculatorEl, {
                notes: false,
                folders: false,
                images: false,
                links: false
            }));

            const explanationDragEl = document.getElementById('explanationDrag');
            const explanationHeaderEl = document.getElementById('explanationHeader');
            if (explanationDragEl && explanationHeaderEl) {
                dragMove(explanationDragEl, explanationHeaderEl);
            }

            // dragMove(document.getElementById('calculatorDrag'), document.getElementById('calculatorHeader'));
            const calculatorDragEl = document.getElementById('calculatorDrag');
            const calculatorHeaderEl = document.getElementById('calculatorHeader');
            if (calculatorDragEl && calculatorHeaderEl) {
                dragMove(calculatorDragEl, calculatorHeaderEl);
            }
        }
    }, [testModules]);

    useLayoutEffect(() => {
        if (testModules) {
            renderKatex();
            setHighlighting([]);
            setFullPassage(null);
            const passageData = testModules[moduleIndex].questions[questionIndex]?.passages;
            if (passageData) {
                const { id, indices, passageFigures } = testModules[moduleIndex].questions[questionIndex].passages
                if (indices) generateHightlightIndexing({ id, indices, passageFigures });  
                getPassageWithFigure({ id, passageFigures });
            }
        }
    }, [testModules, moduleIndex, questionIndex]);

    useEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [directionsOpen]);

    useEffect(() => {
        if (calculatorOpen) {
            calculator.focusFirstExpression();
        }
    }, [calculatorOpen]);

    if (testModules === null) return <Loader />;

    const {
        name,
        questions,
        subject,
        prompt
    } = testModules[moduleIndex];

    return (
        <>
        <Helmet> 
            <title>
                Test Preview - Test Center Admin
            </title>
        </Helmet>
        <Header title={testName} prevRoutes={[{ name: 'All Tests', path: '/tests' }]}/>
        <div className='testACTPreview__box' onCopy={(event) => { event.preventDefault() }}>
            {/* screen that will be appear when click button is working */}
            {isPrint &&
                <div className='testACTPreview__print' ref={printRef}>
                    {testModules.map((testModule, m) => (<>
                        <h2 className={m > 0
                            ? 'testACTPreview__title testACTPreview__pageBreakBefore'
                            : 'testACTPreview__title'
                        }>
                            {testModule.subject.replace('ACT ', '')}
                        </h2>
                        {testModule.questions.map((question, q) => {
                            return (
                                <>
                                    <div className='testACTPreview__test testPreview__pageBreak'>
                                        {/* {question.passages && */}
                                        {(question.passages && testModule.questions.findIndex(moduleQuestion => moduleQuestion.passages?.id === question.passages.id) === q) && 
                                        <>
                                            <div className='testACTPreview__materialContent'>
                                                <div key={question.passages.id} className='testACTPreview__passage'>
                                                    <p
                                                        id={`print ${question.passages.id}`}
                                                        className='katex testACTPreview__passageText'
                                                    >
                                                        {question.passages.passage}
                                                    </p>
                                                    {printFullPassage && printFullPassage[question.passages.id] && <p
                                                        id={`passage ${question.passages.id}`}
                                                        className='katex testACTPreview__passageTextPassage'
                                                        dangerouslySetInnerHTML={{ __html: printFullPassage[question.passages.id].html || 'yes' }}
                                                    />}
                                                    {printHighlighting && printHighlighting[question.passages.id] && printHighlighting[question.passages.id].map((html, index) => (
                                                        <div
                                                            id={`highlighting ${question.passages.id}`}
                                                            key={index}
                                                            className='katex testACTPreview__Highlight'
                                                            dangerouslySetInnerHTML={{ __html: html }}
                                                        ></div>
                                                    ))}
                                                </div>
                                            </div>
                                        </>}
                                        <div className='testACTPreview__question'>
                                            <div className='testACTPreview__questionContent'>
                                                <div className='testACTPreview__questionHeader'>
                                                    <p className="testACTPreview__questionNumber">
                                                        {q + 1}
                                                    </p>
                                                </div>

                                                <div id='testACTPreview__questionChoices' className='testACTPreview__questionChoices'>
                                                    {testModule.subject === 'ACT Mathematics' &&
                                                        question.figures.map(figure => (
                                                            <img
                                                                className='testACTPreview__figure'
                                                                key={figure.id}
                                                                src={figure.image}
                                                                alt={(q + 1).toString()}
                                                            />
                                                        ))
                                                    }
                                                    <p className='testACTPreview__questionQuestion'>
                                                        <span className='katex'>
                                                            {question.question}
                                                        </span>
                                                    </p>
                                                    {question.choices.length
                                                    &&  // multiple choice answer
                                                    <>
                                                            {question.choices.map((choice, i) => (
                                                                <div key={choice.id}  
                                                                    className='testACTPreview__questionChoiceWrapper'
                                                                >
                                                                    <div 
                                                                        className='testACTPreview__choiceContainer'
                                                                    >
                                                                        <div
                                                                            className={(choice.isCorrect && (showAnswer || !cleanVersionEnabled))
                                                                                ? 'testACTPreview__radioButton--selected'
                                                                                : 'testACTPreview__radioButton'
                                                                            }>
                                                                                {(choice.isCorrect && (showAnswer || !cleanVersionEnabled))
                                                                                    ? <IoMdRadioButtonOn className='testACTPreview__radioButtonIcon'/>
                                                                                    : <IoMdRadioButtonOff className='testACTPreview__radioButtonIcon'/>
                                                                                }
                                                                        </div>
                                                                        <div className={(choice.isCorrect && (showAnswer || !cleanVersionEnabled))
                                                                            ? 'testACTPreview__questionChoiceContent--selected'
                                                                            : 'testACTPreview__questionChoiceContent'
                                                                        }>
                                                                            <p className='testACTPreview__choiceText'>
                                                                                {MULTIPLE_CHOICES[i]}.
                                                                            </p>
                                                                            {choice.figure
                                                                            ?   <img
                                                                                    className='testACTPreview__questionChoiceFigure'
                                                                                    key={choice.figure}
                                                                                    src={choice.figure}
                                                                                    alt={choice.figure}
                                                                                />
                                                                            :   <p className='testACTPreview__questionChoiceText'>
                                                                                    <span className='katex'>
                                                                                        {choice.choice}
                                                                                    </span>
                                                                                </p>
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </>
                                                    }
                                                    <div className='testACTPreview__marginBottom'></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {showExplanation && <>
                                        <div>Explanation: </div>
                                        {question.explanationFigure && (
                                            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                                <img
                                                    src={question.explanationFigure}
                                                    alt="Explanation Figure"
                                                    style={{ maxWidth: question.explanation ? '70%' : '100%', height: 'auto' }}
                                                />
                                            </div>
                                        )}
                                        {question.explanation && 
                                            <div className='testACTPreview__printExpl'>
                                                <span className='katex'>{question.explanation}</span>
                                            </div>
                                        }
                                    </>}
                                </>
                            )
                        })}
                    </>))}
                </div>
            }

            <div className='testACTPreview'>
                {/* test Header */}
                <div className='testACTPreview__header'>
                    <div className='testACTPreview__titleGroup'>
                        <button className='testACTPreview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='testACTPreview__title'>
                                {subject.replace('ACT ', '')}
                            </h1>
                            {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='testACTPreview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='testACTPreview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='testACTPreview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((testModule, t) => (
                                        <p
                                            className={t === moduleIndex
                                                ? 'testACTPreview__moduleSelectDropdownItem--selected'
                                                : 'testACTPreview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {testModule.subject.replace('ACT ', '')}
                                        </p>
                                    ))}
                                </div>
                            }
                        </button>
                        <button className='testACTPreview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='testACTPreview__directionsIcon' />
                                : <MdKeyboardArrowDown className='testACTPreview__directionsIcon' />
                            }
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='testACTPreview__directions' ref={directionsRef}>
                            <div className='testACTPreview__directionsBubble'>
                                <p className='katex testACTPreview__directionsText'>
                                    {prompt
                                        ? prompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='testACTPreview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }

                    {/* header action */}
                    <div className='testACTPreview__actionGroup'>
                        {!(user.organizations.some(organization => DISABLE_PRINT.includes(organization))
                            || user.customizations.find(customization => customization === 'Disable Print')
                        ) &&
                            <button
                                className='testACTPreview__action'
                                onClick={onPrint}
                            >
                                <MdOutlinePrint className='testACTPreview__actionIcon' />
                                <p className='testACTPreview__actionText'>Print</p>
                            </button>
                        }
                        {subject === 'ACT Mathematics' && <>
                            <button 
                                className={calculatorOpen
                                    ? 'testACTPreview__action--active'
                                    : 'testACTPreview__action'
                                }
                                onClick={() => {
                                    setCalculatorOpen(!calculatorOpen);
                                }}
                            >
                                <MdOutlineCalculate className='testACTPreview__actionIcon' />
                                <p className='testACTPreview__actionText'>Calculator</p>
                            </button>
                        </>}
                        <button
                            className='testACTPreview__action'
                            onClick={() => navigate(-1)}
                        >
                            <MdExitToApp className='testACTPreview__actionIcon' />
                            <p className='testACTPreview__actionText'>Exit</p>
                        </button>
                    </div>
                    <div id='explanationDrag' className={showExplanation
                        ? 'testACTPreview__popup--explanation'
                        : 'testACTPreview__popup--hidden'
                    }>
                        <div id='explanationHeader' className='testACTPreview__popupHeader'>
                            <h3 className='testACTPreview__popupHeaderText'>Explanation</h3>
                            <MdClose className='testACTPreview__popupHeaderIcon' onClick={() => setShowExplanation(false)} />
                        </div>
                        <div className='testACTPreview__explanationContainer'>
                            {questions[questionIndex].explanationFigure && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <img
                                        src={questions[questionIndex].explanationFigure}
                                        alt="Explanation Figure"
                                        style={{ maxWidth: questions[questionIndex].explanation ? '80%' : '100%', height: 'auto' }}
                                    />
                                </div>
                            )}
                            <div className='testACTPreview__explanationBox katex'>
                                {questions[questionIndex].explanation
                                    ? questions[questionIndex].explanation
                                    : questions[questionIndex].explanationFigure
                                    ? ''
                                    : 'No explanation'
                                }
                            </div>
                        </div>
                    </div>
                    <div id='calculatorDrag' className={calculatorOpen
                        ? 'testACTPreview__popup--calculator'
                        : 'testACTPreview__popup--hidden'
                    }>
                        <div id='calculatorHeader' className='testACTPreview__popupHeader'>
                            <h3 className='testACTPreview__popupHeaderText'>Scientific Calculator</h3>
                            <MdClose className='testACTPreview__popupHeaderIcon' onClick={() => setCalculatorOpen(false)} />
                        </div>
                        {/* <ScientificCalculator /> */}
                        <div
                            id='calculator'
                            style={{ width: 400, height: 600 }}
                        ></div>
                    </div>
                </div>
                <div className='testACTPreview__dottedLine'></div>


                {/* test content */}
                <div className='testACTPreview__test'>
                    {/* left-passage */}
                    {questions[questionIndex].passages && <>
                        <div className='testACTPreview__material' ref = {passageContainerRef}>
                            <div className='testACTPreview__materialContent'>
                                <div key={questions[questionIndex].passages.id} className='testACTPreview__passage'>
                                    {/* 해당 id를 가진 passage의 p태그 가져와서 highlight 넣은 html 받아와서 겹쳐 보여줌 (z-index, transparent 조절) */}
                                    <p
                                        id={questions[questionIndex].passages.id}
                                        className='katex testACTPreview__passageText'
                                    >
                                        {questions[questionIndex].passages.passage}
                                    </p>
                                    <p
                                        id={`passage ${questions[questionIndex].passages.id}`}
                                        className='katex testACTPreview__passageTextPassage'
                                        dangerouslySetInnerHTML={{ __html: fullPassage?.html }}
                                    >
                                    </p>
                                    {/* {questionNumber > 0 &&  */}
                                    {highlighting.length > 0 &&
                                        highlighting.map((highlight, index)=>
                                            <div
                                                id={`highlighting ${questions[questionIndex].passages.id}`}
                                                key={index}
                                                className='katex testACTPreview__Highlight'
                                                dangerouslySetInnerHTML={{ __html: highlight?.html }}
                                            ></div>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                        <div className='testACTPreview__verticalLine'></div>
                    </>}
                    
                    {/* right-question */}
                    <div className='testACTPreview__question'>
                        <div className='testACTPreview__questionContent'>
                            {/* header */}
                            <div className='testACTPreview__questionHeader'>
                                <p className="testACTPreview__questionNumber">
                                    {questionIndex + 1}
                                </p>
                                <div className='testACTPreview__questionButtons'>
                                    {(questions[questionIndex].explanation || questions[questionIndex].explanationFigure) &&
                                        <button
                                                className={showExplanation
                                                    ? 'testACTPreview__explanation--active'
                                                    : 'testACTPreview__explanation'
                                                }
                                                onClick={() => setShowExplanation(!showExplanation)}
                                            >
                                                Explanation
                                        </button>
                                    }
                                    <button
                                        className={showAnswer
                                            ? 'testACTPreview__showAnswer--active'
                                            : 'testACTPreview__showAnswer'
                                        }
                                        onClick={() => setShowAnswer(!showAnswer)}
                                    >
                                        {showAnswer ? 'Hide answer' : 'Show answer'}
                                    </button>
                                </div>
                            </div>

                            <div id='testACTPreview__questionChoices' className='testACTPreview__questionChoices'>
                                {/* question content */}
                                <p className='testACTPreview__questionQuestionContainer'>
                                    <span className='katex testACTPreview__questionQuestion'>
                                        {questions[questionIndex].question}
                                    </span>
                                    {questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='testACTPreview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                        />
                                    ))}
                                </p>
                                {questions[questionIndex].choices.length
                                ?   // multiple chioice
                                    <>
                                        {questions[questionIndex].choices.length &&   
                                            <>
                                                {questions[questionIndex].choices.map((choice, i) => (
                                                    <div key={choice.id}  
                                                        className='testACTPreview__questionChoiceWrapper'
                                                    >
                                                        <div 
                                                            className='testACTPreview__choiceContainer'
                                                        >
                                                            <div
                                                                className={showAnswer && choice.isCorrect
                                                                    ? 'testACTPreview__radioButton--selected'
                                                                    : 'testACTPreview__radioButton'
                                                                }>
                                                                    {showAnswer && choice.isCorrect?
                                                                        <IoMdRadioButtonOn className='testACTPreview__radioButtonIcon'/>
                                                                        : <IoMdRadioButtonOff className='testACTPreview__radioButtonIcon'/>
                                                                    }
                                                            </div>
                                                            <div className={showAnswer && choice.isCorrect
                                                                ? 'testACTPreview__questionChoiceContent--selected'
                                                                : 'testACTPreview__questionChoiceContent'
                                                            }>
                                                                <p className='testACTPreview__choiceText'>
                                                                    {MULTIPLE_CHOICES[i]}.
                                                                </p>
                                                                {choice.figure
                                                                ?   <img
                                                                        className='testACTPreview__questionChoiceFigure'
                                                                        key={choice.figure}
                                                                        src={choice.figure}
                                                                        alt={choice.figure}
                                                                    />
                                                                :   <p className='testACTPreview__questionChoiceText'>
                                                                        <span className='katex'>
                                                                            {choice.choice}
                                                                        </span>
                                                                    </p>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        }
                                    </>
                                : // input answer
                                   <div className='testACTPreview__questionAnswerReview'>
                                        <TextInput
                                            className='testACTPreview__questionAnswer'
                                            inputClassName='testACTPreview__questionAnswerInput'
                                            name={name + questionIndex}
                                            value={showAnswer? questions[questionIndex].correctAnswers.toString().replaceAll(',', ', '): ''}
                                        />
                                    </div>
                                }
                                <div className='testACTPreview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='testACTPreview__dottedLine'></div>
                
                {/* text content bottom */}
                <div className='testACTPreview__footer'>
                    <button
                        className='testACTPreview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='testACTPreview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='testACTPreview__navigatorToggleIcon' />
                        }
                    </button>

                    <div className='testACTPreview__buttons'>
                        <Button
                            className='testACTPreview__button'
                            secondary
                            disabled={moduleIndex === 0 && questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='testACTPreview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>

                    {/* bottom 'Question' button opened */}
                    {navigatorOpen &&
                        <div className='testACTPreview__navigator' ref={navigatorRef}>
                            <div className='testACTPreview__navigatorBubble'>
                                <h2 className='testACTPreview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='testACTPreview__navigatorLegends'>
                                    <div className='testACTPreview__navigatorLegend'>
                                        <MdOutlineLocationOn className='testACTPreview__navigatorLegendCurrent' />
                                        <p className='testACTPreview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                </div>
                                <div className='testACTPreview__navigatorItems'>
                                    {questions.map((question, i) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className="testACTPreview__navigatorItem--correct"
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='testACTPreview__navigatorItemNumber'>{i + 1}</u>
                                                : <p className='testACTPreview__navigatorItemNumber'>{i + 1}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='testACTPreview__navigatorItemCurrent' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }

                </div>
            </div>
        </div>
        </>
    );
}

export default TestACTPreview;